/**
 * 병원 서비스 클래스
 * 병원 검색 및 데이터 처리 로직을 분리하여 관리합니다.
 */
export default class HospitalService {
  /**
   * 병원 목록 조회
   * @param {Object} apiInstance - API 인스턴스
   * @param {Object} params - 검색 파라미터
   * @returns {Promise<{list: Array, totalDocs: number, totalPages: number}>} 병원 목록과 총 개수
   */
  static async getHospitalList(apiInstance, params) {
    try {
      const response = await apiInstance.post('/companies/certi-admin/category-bind/hospitals', params);
      
      if (response.data) {
        // 응답 데이터 처리
        const list = response.data.docs.map(hospital => ({
          id: hospital._id,
          name: hospital.name,
          address: hospital.addrOne && hospital.addrTwo 
            ? `${hospital.addrOne} ${hospital.addrTwo}${hospital.addrDetail ? ' ' + hospital.addrDetail : ''}`
            : (hospital.bizAddr || '')
        }));
        
        return {
          list,
          totalDocs: response.data.totalDocs || 0,
          totalPages: response.data.totalPages || 0
        };
      }
      
      return { list: [], totalDocs: 0, totalPages: 0 };
    } catch (error) {
      console.error('병원 목록 조회 실패:', error);
      throw error;
    }
  }
  
  /**
   * 제조사 목록 조회
   * @param {Object} apiInstance - API 인스턴스
   * @param {Object} params - 검색 파라미터
   * @returns {Promise<{list: Array, totalDocs: number, totalPages: number}>} 제조사 목록과 총 개수
   */
  static async getManufacturerList(apiInstance, params) {
    try {
      const response = await apiInstance.post('/companies/certi-admin/category-bind/manufacturers', params);
      
      if (response.data) {
        // 응답 데이터 처리
        const list = response.data.docs.map(manufacturer => ({
          id: manufacturer._id,
          name: manufacturer.name,
          address: manufacturer.addrOne && manufacturer.addrTwo 
            ? `${manufacturer.addrOne} ${manufacturer.addrTwo}${manufacturer.addrDetail ? ' ' + manufacturer.addrDetail : ''}`
            : (manufacturer.bizAddr || '')
        }));
        
        return {
          list,
          totalDocs: response.data.totalDocs || 0,
          totalPages: response.data.totalPages || 0
        };
      }
      
      return { list: [], totalDocs: 0, totalPages: 0 };
    } catch (error) {
      console.error('제조사 목록 조회 실패:', error);
      throw error;
    }
  }
  
  /**
   * 검색 파라미터 구성
   * @param {Object} options - 검색 옵션
   * @param {number} options.currentPage - 현재 페이지
   * @param {number} options.rowsPerPage - 페이지당 행 수
   * @param {string} options.condition - 검색 조건
   * @param {string} [options.type='HOSPITAL'] - 기관 타입(HOSPITAL 또는 MANUFACTURER)
   * @param {string} [options.viewManufacturerId] - 제조사 ID (제조사 소속 병원 검색 시 사용)
   * @returns {Object} API 요청 파라미터
   */
  static getSearchParams(options) {
    const { currentPage, rowsPerPage, condition, type = 'HOSPITAL', viewManufacturerId } = options;
    
    const params = {
      page: currentPage,
      limit: rowsPerPage,
      type: type // type 파라미터 사용, 기본값은 'HOSPITAL'
    };
    
    // 검색어가 있는 경우 추가
    if (condition) {
      params.search = 'name';
      params.condition = condition;
    }
    
    // 제조사 ID가 있는 경우 추가
    if (viewManufacturerId) {
      params.viewManufacturerId = viewManufacturerId;
    }
    
    return params;
  }
} 